import { Injectable } from '@angular/core';
import jsPDF from 'jspdf';
import { ApiService } from '../../services/api.service';
import { ConversationJson, ConversationMessage } from './chat-bot.models';

// â”€â”€â”€ PDF Layout Constants â”€â”€â”€â”€â”
const PDF_MARGIN      = 10;                           // mm â€“ all-side margin
const PDF_PAGE_W      = 210;                          // mm â€“ A4 width
const PDF_PAGE_H      = 297;                          // mm â€“ A4 height
const PDF_CONTENT_W   = PDF_PAGE_W - 2 * PDF_MARGIN; // 190 mm usable width
const BUBBLE_MAX_FRAC = 0.72;                         // bubble = 72 % of content width
const BUBBLE_PAD      = 3;                            // mm â€“ inner bubble padding
const MSG_FS          = 9;                            // pt â€“ message body font size
const SENDER_FS       = 7;                            // pt â€“ sender label font size
const TS_FS           = 7;                            // pt â€“ timestamp font size
const LINE_H          = 4.5;                          // mm â€“ line height for body text
const SENDER_H        = 5;                            // mm â€“ reserved for sender row
const TS_H            = 5;                            // mm â€“ reserved for timestamp row
const MSG_GAP         = 5;                            // mm â€“ gap between messages

@Injectable({ providedIn: 'root' })
export class ChatBotHelperService {

  constructor(private apiService: ApiService) {}

  // â”€â”€â”€ UNCHANGED: content formatters â”€â”€â”€â”€

  private formatMessageContent(content: string, inline = false): string {
    if (!content) return '';

    // Normalize line endings and collapse consecutive blank lines.
    content = content.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
    content = content.replace(/\n\s*\n+/g, '\n');

    if (!inline) {
      return content
        .replace(/^# (.*)$/gm,     '<div class="pdf-h1">$1</div>')
        .replace(/^## (.*)$/gm,    '<div class="pdf-h2">$1</div>')
        .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
        .replace(/\*(.*?)\*/g,     '<em>$1</em>')
        .replace(/`(.*?)`/g,       '<code>$1</code>')
        .replace(/\n/g,            '<br/>');
    }

    // Inline mode: no block-level wrappers (used inside table cells).
    return content
      .replace(/^# (.*)$/gm,     '<strong>$1</strong>')
      .replace(/^## (.*)$/gm,    '<strong>$1</strong>')
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      .replace(/\*(.*?)\*/g,     '<em>$1</em>')
      .replace(/`(.*?)`/g,       '<code>$1</code>')
      .replace(/\n/g,            '<br/>');
  }

  getMessageContent(msg: any): string {
    return this.formatMessageContent(msg.message || '');
  }

  // â”€â”€â”€ UNCHANGED: table HTML builder â”€â”€â”€â”€

  private buildTableHtml(msg: any): string {
    let html = `
      <div style=" width:100%; overflow:visible; page-break-inside:auto; break-inside:auto; ">
        <table style=" width:100%; max-width:100%; border-collapse:collapse; table-layout:fixed; margin-top:10px; font-size:10px; " >
          <thead style=" display:table-header-group; ">
            <tr>`;

    msg.ui.columns.forEach((col: any) => {
      html += `
        <th style=" border:1px solid #000; padding:6px; text-align:left; word-break:break-word; overflow-wrap:break-word; white-space:normal; background:#f5f5f5; ">
          ${col.header}
        </th>`;
    });

    html += `</tr> </thead> <tbody style="page-break-inside:auto;"> `;

    msg.ui.data.forEach((row: any) => {
      html += `<tr>`;
      msg.ui.columns.forEach((col: any) => {
        html += `
          <td style=" border:1px solid #000; padding:6px; word-break:break-word; overflow-wrap:break-word; white-space:normal; vertical-align:top; ">
            ${row[col.key] ? this.formatMessageContent(String(row[col.key]), true) : ''}
          </td>`;
      });
      html += `</tr>`;
    });

    html += ` </tbody> </table> </div>`;
    return html;
  }

  // â”€â”€â”€ UNCHANGED: JSON export â”€â”€â”€â”€â

  buildConversationJson(messages: any[]): ConversationJson {
    const exported: ConversationMessage[] = messages.map((msg) => ({
      sender:     msg.sender as 'user' | 'assistant',
      message:    this.getMessageContent(msg),
      uiType:     msg.uiType ?? null,
      timestamp:  msg.timestamp ? new Date(msg.timestamp) : null,
      formatTime: msg.formatTime ?? ''
    }));
    return { exportedAt: new Date().toISOString(), messages: exported };
  }

  // â”€â”€â”€ Plain-text extractor for jsPDF â”€â”€â”€â”€â”€â”€

  private getPlainText(msg: any): string {
    return (msg.message || '')
      .replace(/\r\n/g, '\n').replace(/\r/g, '\n')
      .replace(/<br\s*\/?>/gi, '\n')   // convert <br> / <br/> to newline
      .replace(/<[^>]+>/g, '')         // strip any remaining HTML tags
      .replace(/\n\s*\n+/g, '\n')
      .replace(/^#{1,6}\s+/gm, '')
      .replace(/\*\*(.*?)\*\*/g, '$1')
      .replace(/\*(.*?)\*/g, '$1')
      .replace(/`(.*?)`/g, '$1')
      .trim();
  }

  private getCellPlainText(value: any): string {
    return String(value ?? '')
      .replace(/\r\n/g, '\n').replace(/\r/g, '\n')
      .replace(/<br\s*\/?>/gi, '\n')
      .replace(/<[^>]+>/g, '')
      .replace(/\n\s*\n+/g, '\n')
      .replace(/^#{1,6}\s+/gm, '')
      .replace(/\*\*(.*?)\*\*/g, '$1')
      .replace(/\*(.*?)\*/g, '$1')
      .replace(/`(.*?)`/g, '$1')
      .trim();
  }

  // â”€â”€â”€ PDF: draw conversation header, returns y after header â”€

  private drawHeader(pdf: jsPDF, margin: number, contentW: number, exportedAt: string): number {
    let y = margin;
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(14);
    pdf.setTextColor(25, 118, 210);
    pdf.text('Conversation', margin, y + 7);
    y += 10;
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(8);
    pdf.setTextColor(102, 102, 102);
    pdf.text(exportedAt, margin, y + 4);
    y += 7;
    pdf.setDrawColor(25, 118, 210);
    pdf.setLineWidth(0.5);
    pdf.line(margin, y, margin + contentW, y);
    return y + 5;
  }

  // â”€â”€â”€ PDF: calculate message block height without drawing â”

  private calcBlockHeight(pdf: jsPDF, msg: any, contentW: number, includeTimestamp = true): number {
    const text    = this.getPlainText(msg);
    const bubbleW = contentW * BUBBLE_MAX_FRAC;
    const innerW  = bubbleW - 2 * BUBBLE_PAD;
    pdf.setFontSize(MSG_FS);
    const lines   = pdf.splitTextToSize(text || ' ', innerW);
    const bubbleH = lines.length * LINE_H + 2 * BUBBLE_PAD;
    return SENDER_H + bubbleH + (includeTimestamp ? TS_H : 0);
  }

  // â”€â”€â”€ PDF: draw one chat message block, returns new y â”€â

  private drawMessage(pdf: jsPDF, msg: any, y: number, margin: number, contentW: number, skipTimestamp = false): number {
    const isUser     = msg.sender === 'user';
    const label      = isUser ? 'You' : 'Iris';
    const text       = this.getPlainText(msg);
    const ts         = msg.formatTime ? (msg.formatTime.split(' - ')[1] || msg.formatTime) : '';
    const maxBubbleW = contentW * BUBBLE_MAX_FRAC;
    const innerW     = maxBubbleW - 2 * BUBBLE_PAD;
    const align      = isUser ? ('right' as const) : ('left' as const);
    const textX      = isUser ? margin + contentW : margin;

    // Sender label
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(SENDER_FS);
    pdf.setTextColor(100, 100, 100);
    pdf.text(label, textX, y + 3.5, { align });
    y += SENDER_H;

    // Word-wrap message text
    pdf.setFontSize(MSG_FS);
    const lines = pdf.splitTextToSize(text || ' ', innerW);

    // Auto-width: size bubble to content, capped at maxBubbleW
    const longestLineW = Math.max(...(lines as string[]).map((l: string) => pdf.getTextWidth(l)));
    const bubbleW  = Math.min(longestLineW + 2 * BUBBLE_PAD, maxBubbleW);
    const bubbleX  = isUser ? margin + contentW - bubbleW : margin;
    const bubbleH  = lines.length * LINE_H + 2 * BUBBLE_PAD;

    // Bubble background
    if (isUser) {
      pdf.setFillColor(25, 118, 210);
    } else {
      pdf.setFillColor(240, 240, 240);
    }
    pdf.roundedRect(bubbleX, y, bubbleW, bubbleH, 2, 2, 'F');

    // Bubble text
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(MSG_FS);
    if (isUser) {
      pdf.setTextColor(255, 255, 255);
    } else {
      pdf.setTextColor(51, 51, 51);
    }
    pdf.text(lines, bubbleX + BUBBLE_PAD, y + BUBBLE_PAD + LINE_H * 0.8);
    y += bubbleH + 1;

    // Timestamp — skipped for table messages (rendered after the table instead)
    if (!skipTimestamp) {
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(TS_FS);
      pdf.setTextColor(170, 170, 170);
      pdf.text(ts, textX, y + 3, { align });
      y += TS_H + MSG_GAP;
    }

    return y;
  }

  // ─── PDF: draw timestamp only (used after table rendering) ──────────────────

  private drawTimestamp(pdf: jsPDF, msg: any, y: number, margin: number, contentW: number): number {
    const isUser = msg.sender === 'user';
    const ts     = msg.formatTime ? (msg.formatTime.split(' - ')[1] || msg.formatTime) : '';
    const align  = isUser ? ('right' as const) : ('left' as const);
    const textX  = isUser ? margin + contentW : margin;
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(TS_FS);
    pdf.setTextColor(170, 170, 170);
    pdf.text(ts, textX, y + 3, { align });
    return y + TS_H + MSG_GAP;
  }

  // ─── PDF: draw KV message as one unified bubble ─────────────────────────────

  private calcKvBlockHeight(pdf: jsPDF, msg: any, contentW: number): number {
    const text    = this.getPlainText(msg);
    const items: Array<any> = msg.ui?.data || [];
    const innerW  = contentW * BUBBLE_MAX_FRAC - 2 * BUBBLE_PAD;
    pdf.setFontSize(MSG_FS);
    const lines   = pdf.splitTextToSize(text || ' ', innerW);
    const textH   = lines.length * LINE_H;
    const kvH     = items.length > 0 ? (items.length * 5 + 3 * 2 + 3) : 0;
    return SENDER_H + textH + 2 * BUBBLE_PAD + kvH + TS_H;
  }

  private drawMessageWithKv(pdf: jsPDF, msg: any, y: number, margin: number, contentW: number): number {
    const text     = this.getPlainText(msg);
    const ts       = msg.formatTime ? (msg.formatTime.split(' - ')[1] || msg.formatTime) : '';
    const items: Array<{label: string; value: string}> = msg.ui?.data || [];
    const bubbleW  = contentW * BUBBLE_MAX_FRAC;
    const innerW   = bubbleW - 2 * BUBBLE_PAD;
    const KV_ROW_H = 5;
    const KV_PAD   = 3;
    const DIVIDER_H = 3;

    // Sender label
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(SENDER_FS);
    pdf.setTextColor(100, 100, 100);
    pdf.text('Iris', margin, y + 3.5);
    y += SENDER_H;

    // Word-wrap message text
    pdf.setFontSize(MSG_FS);
    const lines   = pdf.splitTextToSize(text || ' ', innerW);
    const textH   = lines.length * LINE_H;
    const kvH     = items.length > 0 ? (items.length * KV_ROW_H + KV_PAD * 2 + DIVIDER_H) : 0;
    const bubbleH = textH + 2 * BUBBLE_PAD + kvH;

    // Single unified bubble background
    pdf.setFillColor(240, 240, 240);
    pdf.roundedRect(margin, y, bubbleW, bubbleH, 2, 2, 'F');

    // Message text inside bubble
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(MSG_FS);
    pdf.setTextColor(51, 51, 51);
    pdf.text(lines, margin + BUBBLE_PAD, y + BUBBLE_PAD + LINE_H * 0.8);

    // KV rows inside the same bubble, separated by a divider
    if (items.length > 0) {
      let kvY = y + BUBBLE_PAD + textH + DIVIDER_H;

      pdf.setDrawColor(200, 200, 200);
      pdf.setLineWidth(0.3);
      pdf.line(margin + BUBBLE_PAD, kvY - 1, margin + bubbleW - BUBBLE_PAD, kvY - 1);
      kvY += KV_PAD;

      pdf.setFontSize(8);
      items.forEach((item) => {
        const kvLabel = `${item.label}: `;
        pdf.setFont('helvetica', 'bold');
        pdf.setTextColor(55, 65, 81);
        pdf.text(kvLabel, margin + BUBBLE_PAD, kvY + 3.5);
        const labelW = pdf.getTextWidth(kvLabel); // measure while bold font is active

        pdf.setFont('helvetica', 'normal');
        pdf.setTextColor(17, 24, 39);
        pdf.text(String(item.value ?? ''), margin + BUBBLE_PAD + labelW, kvY + 3.5);
        kvY += KV_ROW_H;
      });
    }

    y += bubbleH + 1;

    // Timestamp
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(TS_FS);
    pdf.setTextColor(170, 170, 170);
    pdf.text(ts, margin, y + 3);
    return y + TS_H + MSG_GAP;
  }

  // â”€â”€â”€ PDF: render table directly with jsPDF, returns y after table â”

  private async renderTable(pdf: jsPDF, msg: any, currentY: number, margin: number, contentWmm: number): Promise<number> {
    const columns: Array<any> = msg.ui?.columns || [];
    const rows: Array<any> = msg.ui?.data || [];
    if (!columns.length || !rows.length) return currentY;

    const tableTopGap = 3;
    const cellPad = 2;
    const tableFontSize = 7;
    const lineH = 3.6;
    const minRowH = 8;
    const colW = contentWmm / columns.length;
    const pageBottom = PDF_PAGE_H - margin;
    let y = currentY + tableTopGap;

    const drawHeader = () => {
      const headerLines = columns.map((col) => (
        pdf.splitTextToSize(this.getCellPlainText(col.header || col.label || col.key), colW - cellPad * 2) as string[]
      ));
      const headerH = Math.max(minRowH, ...headerLines.map(lines => lines.length * lineH + cellPad * 2));

      if (y + headerH > pageBottom) {
        pdf.addPage();
        y = margin;
      }

      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(tableFontSize);
      pdf.setDrawColor(0, 0, 0);
      pdf.setLineWidth(0.1);

      headerLines.forEach((lines, colIndex) => {
        const x = margin + colIndex * colW;
        pdf.setFillColor(245, 245, 245);
        pdf.setTextColor(51, 51, 51);
        pdf.rect(x, y, colW, headerH, 'FD');
        pdf.text(lines.length ? lines : [''], x + cellPad, y + cellPad + 2.5);
      });

      y += headerH;
    };

    drawHeader();

    rows.forEach((row) => {
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(tableFontSize);

      const cellLines = columns.map((col) => (
        pdf.splitTextToSize(this.getCellPlainText(row[col.key]), colW - cellPad * 2) as string[]
      ));
      const rowH = Math.max(minRowH, ...cellLines.map(lines => lines.length * lineH + cellPad * 2));

      if (y + rowH > pageBottom) {
        pdf.addPage();
        y = margin;
      }

      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(tableFontSize);
      pdf.setDrawColor(0, 0, 0);
      pdf.setLineWidth(0.1);
      pdf.setTextColor(51, 51, 51);

      cellLines.forEach((lines, colIndex) => {
        const x = margin + colIndex * colW;
        pdf.rect(x, y, colW, rowH);
        pdf.text(lines.length ? lines : [''], x + cellPad, y + cellPad + 2.5);
      });

      y += rowH;
    });

    return y;
  }

  // â”€â”€â”€ Public: generate PDF blob\

  async generateConversationPdf(messages: any[], exportedAt: string): Promise<Blob> {
    const pdf = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'portrait' });
    let y = this.drawHeader(pdf, PDF_MARGIN, PDF_CONTENT_W, exportedAt);

    for (const msg of messages) {
      if (msg.uiType === 'table') {
        // 1. Draw sender + bubble (no timestamp yet)
        if (msg.message) {
          const blockH = this.calcBlockHeight(pdf, msg, PDF_CONTENT_W, false);
          if (y + blockH > PDF_PAGE_H - PDF_MARGIN) {
            pdf.addPage();
            y = PDF_MARGIN;
          }
          y = this.drawMessage(pdf, msg, y, PDF_MARGIN, PDF_CONTENT_W, true);
        }
        // 2. Render table starting at current y — uses remaining space first
        y = await this.renderTable(pdf, msg, y, PDF_MARGIN, PDF_CONTENT_W);
        // 3. Draw timestamp after the table
        y = this.drawTimestamp(pdf, msg, y, PDF_MARGIN, PDF_CONTENT_W);
      } else if (msg.uiType === 'kv') {
        // Single unified bubble containing text + KV rows
        const blockH = this.calcKvBlockHeight(pdf, msg, PDF_CONTENT_W);
        if (y + blockH > PDF_PAGE_H - PDF_MARGIN) {
          pdf.addPage();
          y = PDF_MARGIN;
        }
        y = this.drawMessageWithKv(pdf, msg, y, PDF_MARGIN, PDF_CONTENT_W);
      } else {
        // Chat message: check if block fits, add page if needed
        const blockH = this.calcBlockHeight(pdf, msg, PDF_CONTENT_W);
        if (y + blockH > PDF_PAGE_H - PDF_MARGIN) {
          pdf.addPage();
          y = PDF_MARGIN;
        }
        y = this.drawMessage(pdf, msg, y, PDF_MARGIN, PDF_CONTENT_W);
      }
    }

    return pdf.output('blob') as Blob;
  }

  // â”€â”€â”€ Public: send to CSM (UNCHANGED) 

  async sendConversationToCsm(messages: any[], exportedAt: string): Promise<FormData> {
    const pdfBlob = await this.generateConversationPdf(messages, exportedAt);
    const formData = new FormData();
    formData.append('file', pdfBlob, 'chat-conversation.pdf');
    // const pdfUrl = URL.createObjectURL(pdfBlob);
    // window.open(pdfUrl, '_blank');
    return formData;
  }
}
